/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import InfiniteList from '@comp/InfiniteList/InfiniteList';
import BatchCardNew from '@comp/BatchCard/BatchCardNew';
import Dialog from '@comp/Dialog/Dialog';
import { useSelectorToStore, useDispatchToStore } from '@hooks/reduxHooks';
import { fetchBatches, enrollInBatch } from '@actions/batches';
import Paper from '@base/Paper/Paper';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import BreadCrumbHelper, { goBack } from '@utils/breadcrumbs';
import { getImage } from '@utils/imageHelper';
import { Spacings, breakpoints } from '@styles/index';
import { useSelector } from 'react-redux';
import { batchFeedOptions, getBatchFeedKey } from '@utils/feedTypeHelper';
import useRefreshDataOnGloUpdate from '@hooks/useRefreshDataOnGloUpdate';
import { getCourseEnrollMessage } from '@constants/index';
import { useSnackbar } from '@stores/snackbar';
import {
  getIsSubscribedToOffline,
  getPlusSubscriptionInfoLatest
} from '@selectors/subscription';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import { fetchTopologyInfo } from '@actions/topologies';
import { Typography } from '@material-ui/core';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import Button from 'aquilla/core/molecules/Button';
import useClassTypeFilterOptions from '@api/hooks/batch/useClassTypeFilterOptions';
import ClassTypeDropdown from './ClassTypeDropdown';

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TopBar = styled(Paper)`
  padding-bottom: ${Spacings.SPACING_6B};
  padding-top: ${Spacings.SPACING_6B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: none;
  }
`;

const PillsWrapper = styled.div`
  grid-column: span 18;
  display: flex;
  gap: 12px;
  margin-top: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_4B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    margin-top: 16px;
    margin-bottom: 16px;
    overflow-x: auto;
    white-space: nowrap;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;
const StyledButton = styled(Button)`
  height: 48px;
  width: 168px;
`;

const Empty = styled.div`
  grid-column: span 18;
  height: 50vh;
  text-align: center;
  margin-top: 134px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-top: 101px;
  }
`;

const Pill = styled.div`
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 40px;
  border-radius: 60px;
  padding: 10px 20px;
  opacity: 1;
  background: var(--color-base-1);
  box-shadow: 0px 16px 32px 0px var(--color-shadow);
  border-width: 1px;
  border-style: solid;
  border-color: ${(props) =>
    props.active ? 'var(--color-divider-tertiary)' : 'var(--color-divider)'};
`;

const NoCourses = styled(H4)`
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_2B};
`;
const NoCoursesSpan = styled(P1)`
  color: var(--color-text-secondary);
  margin-bottom: ${Spacings.SPACING_6B};
`;

const EmptyImage = styled.img`
  user-drag: none;
  user-select: none;
`;

const Card = styled(BatchCardNew)`
  margin-bottom: ${Spacings.SPACING_6B};
`;
const StyledTypography = styled(Typography)`
  font-size: 14px;
  line-height: 20px;
  vertical-align: middle;
  color: var(--color-text-primary);
  font-weight: ${(props) => (props.active ? 600 : 400)};
`;
const BatchesListing = () => {
  const router = useRouter();
  const { goalUID } = router.query;
  const dispatch = useDispatchToStore();
  const selectedGoalUID = useSelectedGoalUID();
  const [batchesType, setBatchesType] = useState(0);
  const [classType, setClassType] = useState(null);
  const [unEnrollModalOpen, setUnEnrollModalOpen] = useState(false);
  const batchRef = useRef('');
  const { options: classTypeOptions, hasOptions: showClassTypeDropdown } =
    useClassTypeFilterOptions(goalUID);
  const data = useSelectorToStore(
    ({ paginator }) =>
      paginator.batches[getBatchFeedKey(goalUID, batchesType, classType)] ?? {}
  );
  const goalDetails =
    useSelectorToStore(
      ({ topologies }) => topologies.topologyDetails[goalUID]
    ) ?? {};

  useEffect(() => {
    dispatch(fetchTopologyInfo(goalUID));
  }, []);

  const { open: showSnackbar } = useSnackbar();

  const { isRecordedGoal } = goalDetails;

  const toggleEnroll = useCallback(
    async (batchUid, isEnrolled, batch) => {
      if (isEnrolled) {
        batchRef.current = batch;
        setUnEnrollModalOpen(true);
      } else {
        const { uid, name } = batch?.goal || {};
        await dispatch(enrollInBatch(batchUid, !isEnrolled));
        showSnackbar({
          message: getCourseEnrollMessage({
            type: 'Courses',
            goalUID: uid,
            selectedGoalUID,
            goalName: name,
            isRecordedGoal
          }),
          type: 'success'
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const acceptOnClick = () => {
    dispatch(enrollInBatch(batchRef.current.uid, false));
    setUnEnrollModalOpen(false);
  };

  const onCloseModal = () => setUnEnrollModalOpen(false);

  const optionSelect = (option, resetClassType = false) => {
    const nextClassType = resetClassType ? null : classType;
    if (resetClassType) setClassType(null);
    dispatch(
      fetchBatches(
        goalUID,
        option.value,
        undefined,
        false,
        false,
        nextClassType
      )
    );
    setBatchesType(option.value);
  };

  const onClassTypeSelect = (selectedId) => {
    setClassType(selectedId);
    dispatch(
      fetchBatches(goalUID, batchesType, undefined, false, false, selectedId)
    );
  };

  const renderEmptyState = () => {
    return (
      <Empty>
        <EmptyImage src={getImage('empty-batch-state.svg')} alt="" />
        <NoCourses>No batches found</NoCourses>
        <NoCoursesSpan>
          This section has no batches at the moment.
        </NoCoursesSpan>
        <ButtonWrapper>
          <StyledButton
            onClick={() => optionSelect(batchFeedOptions[0], true)}
            variant="primary"
            label="See all batches"
            size="large"
          />
        </ButtonWrapper>
      </Empty>
    );
  };

  const renderBatches = (batch, index) => (
    <Card
      batch={batch}
      key={batch.uid}
      toggleEnroll={toggleEnroll}
      dataAnalytics="batch"
      index={index}
      hideEnroll
    />
  );
  const loadMore = (resetWithoutEmpty, forceReset) => {
    return dispatch(
      fetchBatches(
        goalUID,
        batchesType,
        true,
        resetWithoutEmpty,
        forceReset,
        classType
      )
    );
  };

  useRefreshDataOnGloUpdate(() => loadMore(true, true));
  const hasSubscription = useSelector(
    getPlusSubscriptionInfoLatest(goalDetails.uid)
  );
  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(goalUID));
  return (
    <>
      <TopBar gridColumn="span 18">
        <BreadCrumbs
          tags={BreadCrumbHelper.getSeeAllTags(
            'goal',
            goalDetails,
            null,
            hasSubscription,
            'batches',
            null,
            isOfflineSubscription
          )}
          onBackClick={goBack}
        />
      </TopBar>
      <PillsWrapper>
        {!isRecordedGoal &&
          batchFeedOptions.map((option) => (
            <Pill
              key={option.value}
              optionValue={option.value}
              active={option.value === batchesType}
              onClick={() => optionSelect(option)}
            >
              <StyledTypography
                variant="p4"
                active={option.value === batchesType}
              >
                {option.label}
              </StyledTypography>
            </Pill>
          ))}
        {showClassTypeDropdown && (
          <ClassTypeDropdown
            options={classTypeOptions}
            selectedClassType={classType}
            onSelect={onClassTypeSelect}
          />
        )}
      </PillsWrapper>
      <Dialog
        onClose={onCloseModal}
        dialogState={unEnrollModalOpen}
        heading="Are you sure you want to unenroll?"
        message="All courses from this batch will be removed from your schedule and you will stop receiving updates related to this batch"
        acceptLabel="Unenroll"
        rejectLabel="Cancel"
        acceptOnClick={acceptOnClick}
        rejectOnClick={onCloseModal}
      />
      <InfiniteList
        data={data}
        loadMore={loadMore}
        _renderItem={renderBatches}
        gridColumn="5/15"
      />
      {!data?.isFetching && data?.results.length === 0 && renderEmptyState()}
    </>
  );
};

export default BatchesListing;
